import { Skeleton } from "@/components/skeleton";
import { formatDate } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export interface CrmAttributionTouchSummary {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  touchedAt: string | null;
}

export interface CrmContactAttributionSummary {
  firstTouch: CrmAttributionTouchSummary | null;
  lastTouch: CrmAttributionTouchSummary | null;
}

interface CrmSourceAttributionCardProps {
  summary: CrmContactAttributionSummary | null;
  isLoading: boolean;
  error: string | null;
  title?: string;
  className?: string;
}

export function CrmSourceAttributionCard({
  summary,
  isLoading,
  error,
  title = "Attribution",
  className,
}: CrmSourceAttributionCardProps) {
  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : error ? (
        <p className="mt-2 text-sm text-[var(--color-destructive)]">{error}</p>
      ) : (
        <div className="mt-3 space-y-3">
          <AttributionTouchSection label="First Touch" touch={summary?.firstTouch ?? null} />
          <AttributionTouchSection label="Last Touch" touch={summary?.lastTouch ?? null} />
        </div>
      )}
    </section>
  );
}

function AttributionTouchSection({
  label,
  touch,
}: {
  label: string;
  touch: CrmAttributionTouchSummary | null;
}) {
  const hasAttribution = Boolean(touch?.source || touch?.medium || touch?.campaign);

  return (
    <div className="rounded-lg bg-[var(--color-background)] px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">{label}</p>
        {touch?.touchedAt ? (
          <span className="text-[11px] text-[var(--color-muted-foreground)]">
            {formatDate(touch.touchedAt)}
          </span>
        ) : null}
      </div>
      {hasAttribution ? (
        <dl className="mt-2 grid gap-1 text-sm">
          <AttributionField label="Source" value={touch?.source ?? null} />
          <AttributionField label="Medium" value={touch?.medium ?? null} />
          <AttributionField label="Campaign" value={touch?.campaign ?? null} />
        </dl>
      ) : (
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">No attribution data</p>
      )}
    </div>
  );
}

function AttributionField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-[var(--color-muted-foreground)]">{label}</dt>
      <dd className="text-right text-[var(--color-foreground)]">{value || "No attribution data"}</dd>
    </div>
  );
}
